/* eslint-disable import/first */
import "react-notifications-component/dist/theme.css";

import './styles/scss/main.scss'
import './styles/fonts-6/css/all.min.css';
import "loaders.css/loaders.min.css";

import {
    createBrowserRouter,
    RouterProvider,
    Route,
    createRoutesFromElements,
} from "react-router-dom";
import React, { Suspense, useEffect, useState } from "react";
import { IdleTimerProvider } from "react-idle-timer";

import ApiAuthorizationRoutes from "./components/api-authorization/ApiAuthorizationRoutes";
import { ApplicationPaths } from "./components/api-authorization/ApiAuthorizationConstants";
import { Layout } from "./components/Layout";
import NotFound from "./components/NotFound";
import ErrorBoundary from "./components/ErrorBoundary";
import GlobalErrorView from "./components/GlobalErrorView";
import TagManager from "react-gtm-module";
import { ThankYou } from "./components/ThankYou/ThankYou";
import routes from "./routes";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import WarningModal from "./components/Shared/WarningModal";
import authService from "./components/api-authorization/AuthorizeService";
import BeePreview from "./components/Bee/BeePreview";
import RouterAwareErrorBoundary from "./components/ErrorBoundary"; 

const loading = (
    <div id="spinner-wrapper d-block">
        <div className="spinner"></div>
    </div>
);

export default function App() {
    const WarningPopupTimeout = 300; // in seconds
    const [showWarning, setShowWarning] = useState(false);
    const [countdown, setCountdown] = useState(WarningPopupTimeout);
    const [sessionTimeoutInMin, setSessionTimeoutInMin] = useState(60);
    const [locked, setLocked] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [idleTimer, setIdleTimer] = useState(null); // Replaces useRef

    useEffect(() => {
        const storedPopupOpen = localStorage.getItem("popupOpen");
        const storedCountdown = localStorage.getItem("popupCountdown");
        const sessionTimeout = localStorage.getItem("user.sessionTimeoutInMin");

        const color = localStorage.getItem('badgeColor') || '#96C0FF';
        document.documentElement.style.setProperty('--badge-color', color);

        const isDark = localStorage.getItem('isBadgeColorDark') || '#0082be';
        const colorIcon = isDark ? '#FFFFFF' : '#000000';
        document.documentElement.style.setProperty('--badge-color-Icon', colorIcon);

        setButtonDisabled(false);

        if (sessionTimeout) {
            setSessionTimeoutInMin(sessionTimeout);
        }

        if (storedPopupOpen === "true") {
            setShowWarning(true);
        }

        if (storedCountdown) {
            setCountdown(WarningPopupTimeout);
        }
    }, []);

    const navigateToReturnUrl = (returnUrl, isTimeout = false) => {
        if (isTimeout) {
            document.cookie = "sessionTimeout=true; path=/;";
        }
        window.location.replace(returnUrl);
    }

    const handleOnIdle = async () => {
        const isAuth = await authService.isAuthenticated();
        if (!isAuth) return;

        setShowWarning(true);
        setLocked(false);
        setCountdown(WarningPopupTimeout);
        setButtonDisabled(false);

        console.log('user is idle');
    };

    const handleLogout = () => {
        console.log('HANDLE LOGOUT');
        setButtonDisabled(true);
        setShowWarning(false);

        if (idleTimer) {
            idleTimer.reset();
        }

        localStorage.removeItem("popupOpen");
        localStorage.removeItem("popupCountdown");

        navigateToReturnUrl("/authentication/logout");
    };

    const handleStaySignedIn = () => {
        console.log('HANDLE STAY SIGN IN');
        setButtonDisabled(true);
        console.log('HANDLE STAY SIGN IN: [LOCKED = FALSE]');
        authService.userManager
            .signinSilent()
            .then((renewedUser) => {
                console.log('HANDLE STAY SIGN IN: [SIGN IN SILENT = DONE]');
                setShowWarning(false);
                setCountdown(WarningPopupTimeout);

                if (idleTimer) {
                    idleTimer.reset();
                }

                setButtonDisabled(false);
                localStorage.removeItem("popupOpen");
                localStorage.removeItem("popupCountdown");

                authService.updateState(renewedUser);
            })
            .catch((error) => {
                console.error("Silent renew error", error);
                setShowWarning(false);
                setCountdown(WarningPopupTimeout);
            });
    };

    useEffect(() => {
        let countdownInterval;
        if (showWarning && !buttonDisabled && countdown > 0) {
            countdownInterval = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        } else if (showWarning && !buttonDisabled && countdown === 0) {
            setShowWarning(false);
            localStorage.removeItem("popupOpen");
            localStorage.removeItem("popupCountdown");
            navigateToReturnUrl("/authentication/logout", true);
        }

        localStorage.setItem("popupOpen", showWarning);
        localStorage.setItem("popupCountdown", countdown);

        return () => clearInterval(countdownInterval);
    }, [showWarning, locked, countdown]);

    const router = createBrowserRouter(
        createRoutesFromElements(
            <Route path="/">
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/test-error" element={<GlobalErrorView />} />
            <Route path="/EmailPreview/:partitionKey" element={<BeePreview />} />
            <Route
            path={ApplicationPaths.ApiAuthorizationPrefix + "/*"}
            element={<ApiAuthorizationRoutes />}
        />

      <Route element={<Layout />}>
        <>
          {routes.map((route, idx) =>
            route.component && (
              <Route
                key={idx}
                path={route.path}
                element={
                  <RouterAwareErrorBoundary>
                    <route.component />
                  </RouterAwareErrorBoundary>
                }
              />
            )
          )}
          <Route
            path="*"
            element={
              <RouterAwareErrorBoundary>
                <NotFound />
              </RouterAwareErrorBoundary>
            }
          />
        </>
      </Route>
    </Route>
  )
);

    useEffect(() => {
        const userGuid = localStorage.getItem("user.guid");
        const userName = localStorage.getItem("user.userName");
        const clientId = localStorage.getItem("selectedClientId");
        if (userGuid && userGuid !== "undefined") {
            const tagManagerArgs = {
                gtmId: `${import.meta.env.VITE_GTM_ID}`,
                dataLayer: {
                    userId: userGuid,
                    username: userName,
                    clientid: clientId,
                },
            };
            TagManager.initialize(tagManagerArgs);
        }
    }, []);

    return (
        <>
            <IdleTimerProvider
            timeout={1000 * 60 * sessionTimeoutInMin}
            onIdle={handleOnIdle}
            debounce={500}
            crossTab={{ emitOnAllTabs: true }}
            onMount={(idleTimerInstance) => setIdleTimer(idleTimerInstance)}
            >
         
            <div id="spinner-wrapper-old">
                <div className="spinner"></div>
            </div>

            {showWarning && (
             <WarningModal
                 locked={locked}
                 countdown={countdown}
                 onLogout={handleLogout}
                 onStaySignedIn={handleStaySignedIn}
                 buttonDisable={buttonDisabled}
             />
         )}

            <Suspense fallback={loading}>
                <DndProvider backend={HTML5Backend}>
                <RouterProvider router={router} />
                </DndProvider>
            </Suspense>
            </IdleTimerProvider>
        </>
    );
}